import { useMemo, useState } from "react";
import { Scales, ArrowsLeftRight, MagnifyingGlass } from "@phosphor-icons/react";

/* All amounts in milliliters / grams (canonical units). */
const VOLUME_UNITS = [
  { id: "cucharadita", label: "Cucharadita (cdta.)", ml: 5 },
  { id: "cucharada", label: "Cucharada (cda.)", ml: 15 },
  { id: "vaso_chico", label: "Vaso pequeño", ml: 150 },
  { id: "vaso", label: "Vaso", ml: 200 },
  { id: "vaso_grande", label: "Vaso grande", ml: 250 },
  { id: "taza", label: "Taza (americana)", ml: 240 },
  { id: "ml", label: "Mililitros", ml: 1 },
  { id: "l", label: "Litros", ml: 1000 },
  { id: "fl_oz", label: "Onza líquida (fl oz)", ml: 29.5735 },
  { id: "cup", label: "Cup (EE.UU.)", ml: 236.588 },
  { id: "pint", label: "Pinta (EE.UU.)", ml: 473.176 },
];

const WEIGHT_UNITS = [
  { id: "g", label: "Gramos", g: 1 },
  { id: "kg", label: "Kilogramos", g: 1000 },
  { id: "oz", label: "Onza (oz)", g: 28.3495 },
  { id: "lb", label: "Libra (lb)", g: 453.592 },
  { id: "stone", label: "Stone", g: 6350.29 },
];

/* Ingredient-specific cup → grams (for solids commonly measured by volume) */
const INGREDIENT_CUPS = [
  { name: "Harina de trigo", cup: 120 },
  { name: "Harina integral", cup: 130 },
  { name: "Azúcar blanco", cup: 200 },
  { name: "Azúcar moreno (compactado)", cup: 220 },
  { name: "Azúcar glas", cup: 130 },
  { name: "Mantequilla", cup: 227 },
  { name: "Aceite de oliva", cup: 218 },
  { name: "Leche", cup: 245 },
  { name: "Nata", cup: 240 },
  { name: "Miel", cup: 340 },
  { name: "Arroz crudo", cup: 195 },
  { name: "Avena en copos", cup: 90 },
  { name: "Cacao en polvo", cup: 85 },
  { name: "Chocolate troceado", cup: 175 },
  { name: "Frutos secos picados", cup: 120 },
  { name: "Queso rallado", cup: 100 },
  { name: "Sal", cup: 290 },
  { name: "Levadura química", cup: 192 },
  { name: "Mantequilla derretida", cup: 240 },
  { name: "Pan rallado", cup: 110 },
];

const OVEN_TEMPS = [
  { f: 250, c: 120, label: "Muy bajo (mantener caliente)" },
  { f: 300, c: 150, label: "Bajo" },
  { f: 325, c: 160, label: "Moderado-bajo" },
  { f: 350, c: 180, label: "Moderado (estándar repostería)" },
  { f: 375, c: 190, label: "Moderado-alto" },
  { f: 400, c: 200, label: "Alto" },
  { f: 425, c: 220, label: "Muy alto" },
  { f: 450, c: 230, label: "Pizza / pan" },
  { f: 475, c: 245, label: "Asar fuerte" },
];

export default function Equivalences() {
  // Volume converter state
  const [volQty, setVolQty] = useState(1);
  const [volFrom, setVolFrom] = useState("cucharada");
  const [volTo, setVolTo] = useState("ml");
  const volResult = useMemo(() => {
    const f = VOLUME_UNITS.find((u) => u.id === volFrom);
    const t = VOLUME_UNITS.find((u) => u.id === volTo);
    if (!f || !t || isNaN(volQty)) return null;
    return (volQty * f.ml) / t.ml;
  }, [volQty, volFrom, volTo]);

  // Weight converter
  const [wQty, setWQty] = useState(1);
  const [wFrom, setWFrom] = useState("oz");
  const [wTo, setWTo] = useState("g");
  const wResult = useMemo(() => {
    const f = WEIGHT_UNITS.find((u) => u.id === wFrom);
    const t = WEIGHT_UNITS.find((u) => u.id === wTo);
    if (!f || !t || isNaN(wQty)) return null;
    return (wQty * f.g) / t.g;
  }, [wQty, wFrom, wTo]);

  // Ingredient search
  const [ingQuery, setIngQuery] = useState("");
  const filteredIng = useMemo(() => {
    if (!ingQuery.trim()) return INGREDIENT_CUPS;
    const q = ingQuery.toLowerCase();
    return INGREDIENT_CUPS.filter((i) => i.name.toLowerCase().includes(q));
  }, [ingQuery]);

  const fmt = (n) => {
    if (n === null || n === undefined) return "—";
    if (n >= 100) return n.toFixed(0);
    if (n >= 10) return n.toFixed(1);
    return n.toFixed(2);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-12" data-testid="equivalences-page">
      <div>
        <p className="eyebrow mb-3">cocina sin dudas</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Equivalencias de medidas</h1>
        <p className="mt-3 text-[hsl(var(--muted-foreground))] max-w-2xl">
          Cuando una receta dice "1 cucharadita" y tú quieres saber cuántos gramos son, aquí lo tienes claro.
        </p>
      </div>

      {/* Volume converter */}
      <section className="rounded-3xl bg-white border border-[hsl(var(--border))] p-6 sm:p-8">
        <p className="eyebrow mb-5 inline-flex items-center gap-2">
          <ArrowsLeftRight size={14} /> conversor de volumen
        </p>
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3 items-end">
          <input
            type="number" step="0.1" value={volQty}
            onChange={(e) => setVolQty(parseFloat(e.target.value) || 0)}
            data-testid="vol-qty"
            className="md:col-span-1 px-4 py-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-lg font-serif focus:outline-none focus:border-[hsl(var(--primary))]"
          />
          <select value={volFrom} onChange={(e) => setVolFrom(e.target.value)} data-testid="vol-from"
            className="md:col-span-2 px-4 py-3 rounded-2xl border border-[hsl(var(--border))] bg-white text-sm focus:outline-none focus:border-[hsl(var(--primary))]">
            {VOLUME_UNITS.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
          </select>
          <div className="md:col-span-1 text-center font-serif italic text-2xl text-[hsl(var(--primary))]">→</div>
          <select value={volTo} onChange={(e) => setVolTo(e.target.value)} data-testid="vol-to"
            className="md:col-span-2 px-4 py-3 rounded-2xl border border-[hsl(var(--border))] bg-white text-sm focus:outline-none focus:border-[hsl(var(--primary))]">
            {VOLUME_UNITS.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
          </select>
          <div className="md:col-span-1 text-right">
            <p className="font-serif text-3xl text-[hsl(var(--foreground))]" data-testid="vol-result">{fmt(volResult)}</p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {VOLUME_UNITS.slice(0, 8).map((u) => (
            <div key={u.id} className="rounded-2xl bg-[hsl(var(--muted))] p-4">
              <p className="text-xs text-[hsl(var(--muted-foreground))]">{u.label}</p>
              <p className="font-serif text-xl mt-1">{u.ml < 1 ? u.ml : Math.round(u.ml)} ml</p>
            </div>
          ))}
        </div>
      </section>

      {/* Weight converter */}
      <section className="rounded-3xl bg-white border border-[hsl(var(--border))] p-6 sm:p-8">
        <p className="eyebrow mb-5 inline-flex items-center gap-2">
          <Scales size={14} /> conversor de peso
        </p>
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3 items-end">
          <input
            type="number" step="0.1" value={wQty}
            onChange={(e) => setWQty(parseFloat(e.target.value) || 0)}
            data-testid="w-qty"
            className="md:col-span-1 px-4 py-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-lg font-serif focus:outline-none focus:border-[hsl(var(--primary))]"
          />
          <select value={wFrom} onChange={(e) => setWFrom(e.target.value)} data-testid="w-from"
            className="md:col-span-2 px-4 py-3 rounded-2xl border border-[hsl(var(--border))] bg-white text-sm focus:outline-none focus:border-[hsl(var(--primary))]">
            {WEIGHT_UNITS.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
          </select>
          <div className="md:col-span-1 text-center font-serif italic text-2xl text-[hsl(var(--primary))]">→</div>
          <select value={wTo} onChange={(e) => setWTo(e.target.value)} data-testid="w-to"
            className="md:col-span-2 px-4 py-3 rounded-2xl border border-[hsl(var(--border))] bg-white text-sm focus:outline-none focus:border-[hsl(var(--primary))]">
            {WEIGHT_UNITS.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
          </select>
          <div className="md:col-span-1 text-right">
            <p className="font-serif text-3xl text-[hsl(var(--foreground))]" data-testid="w-result">{fmt(wResult)}</p>
          </div>
        </div>
      </section>

      {/* Ingredient-specific cups → grams */}
      <section className="rounded-3xl bg-white border border-[hsl(var(--border))] p-6 sm:p-8">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <p className="eyebrow">1 taza de... ¿cuántos gramos?</p>
          <div className="pill-input flex items-center gap-2 max-w-xs">
            <MagnifyingGlass size={16} className="text-[hsl(var(--muted-foreground))]" />
            <input
              value={ingQuery}
              onChange={(e) => setIngQuery(e.target.value)}
              placeholder="Buscar ingrediente…"
              data-testid="ing-search"
              className="bg-transparent outline-none w-full text-sm"
            />
          </div>
        </div>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">Una "taza" americana equivale a 240 ml. La conversión a gramos depende del ingrediente:</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredIng.map((ing) => (
            <div key={ing.name} className="rounded-2xl bg-[hsl(var(--background))] border border-[hsl(var(--border))] p-4 flex items-center justify-between">
              <span className="text-sm font-medium">{ing.name}</span>
              <span className="font-serif text-xl text-[hsl(var(--primary))]">{ing.cup} g</span>
            </div>
          ))}
          {filteredIng.length === 0 && (
            <p className="text-sm text-[hsl(var(--muted-foreground))] col-span-full">Sin resultados. Prueba "harina", "azúcar", "mantequilla"…</p>
          )}
        </div>
      </section>

      {/* Oven temps */}
      <section className="rounded-3xl bg-white border border-[hsl(var(--border))] p-6 sm:p-8">
        <p className="eyebrow mb-5">temperaturas de horno</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
              <tr>
                <th className="py-2 pr-4">Fahrenheit</th>
                <th className="py-2 pr-4">Celsius</th>
                <th className="py-2 pr-4">Tipo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(var(--border))]">
              {OVEN_TEMPS.map((t) => (
                <tr key={t.f}>
                  <td className="py-3 pr-4 font-serif text-lg">{t.f} °F</td>
                  <td className="py-3 pr-4 font-serif text-lg text-[hsl(var(--primary))]">{t.c} °C</td>
                  <td className="py-3 pr-4 text-[hsl(var(--muted-foreground))]">{t.label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-xs text-[hsl(var(--muted-foreground))] text-center pb-8">
        Valores estándar de la industria culinaria. Pequeñas variaciones existen entre regiones.
      </p>
    </div>
  );
}
